import * as styles from "./ProjectEditorWindow.css";

import { useAtomValue } from "jotai";
import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Window,
  WindowContent,
  WindowHeader,
  WindowTitle,
} from "@frontend/components/molecules/Window";
import { Trans } from "@lingui/react/macro";

import { ClosureWeeksImpactDialog } from "./components/ClosureWeeksImpactDialog";
import { ProjectEditorBody } from "./components/ProjectEditorBody";
import { ProjectEditorHeader } from "./components/ProjectEditorHeader";
import {
  type UseProjectEditorOptions,
  useProjectEditor,
} from "./hooks/useProjectEditor";
import { useShellTitle } from "./hooks/useShellTitle";
import { projectEditorAtom } from "./stores/editor";

export type ProjectEditorWindowProps = UseProjectEditorOptions & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const DEFAULT_SIZE = { width: 1180, height: 940 };

const MIN_SIZE = { width: 640, height: 480 };

export function ProjectEditorWindow({
  open,
  onOpenChange,
  projectId = null,
  type = "intervention",
  subscriptionId = null,
  onSaved,
  onCancel,
}: ProjectEditorWindowProps): JSX.Element {
  const { project } = useAtomValue(projectEditorAtom);

  const title = useShellTitle(projectId, project?.type ?? type);

  const { loading, saving, error, conflict, impact, save, cancel } =
    useProjectEditor({
      projectId,
      type,
      subscriptionId,
      onSaved: (savedId) => {
        onOpenChange(false);
        onSaved?.(savedId);
      },
      onCancel: () => {
        onOpenChange(false);
        onCancel?.();
      },
    });

  return (
    <Window
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      defaultSize={DEFAULT_SIZE}
      minSize={MIN_SIZE}
    >
      <WindowHeader>
        <WindowTitle>{title}</WindowTitle>
      </WindowHeader>

      <WindowContent className={styles.content}>
        <ProjectEditorHeader type={type} variant="plain" />

        <ProjectEditorBody
          loading={loading}
          error={error}
          conflict={conflict}
        />

        <footer className={styles.footer}>
          <Button variant="neutral" onClick={cancel}>
            <Trans>Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            loading={saving}
            disabled={loading}
            shortcut="mod+s"
            onClick={() => void save()}
          >
            <Trans>Save</Trans>
          </Button>
        </footer>

        <ClosureWeeksImpactDialog {...impact} />
      </WindowContent>
    </Window>
  );
}
